import { useTranslation } from 'react-i18next';

import { View } from '@actual-app/components/view';
import { css, keyframes } from '@emotion/css';

import { sinMovimiento } from '#components/mobile/inicio/inicio.estilos';
import { color } from '#components/mobile/ui/tokens';
import { useFormat } from '#hooks/useFormat';

import { subidaDe } from './cobros';
import type { SerieCobro } from './cobros';

const aparece = keyframes({
  from: { opacity: 0, transform: 'scale(.3)' },
  to: { opacity: 1, transform: 'scale(1)' },
});
const ola = keyframes({
  '0%, 100%': { transform: 'translateY(0)' },
  '50%': { transform: 'translateY(1.5px)' },
});
const claseNudo = css({
  transformBox: 'fill-box',
  transformOrigin: 'center',
  animation: `${aparece} .45s cubic-bezier(.3,1.4,.5,1) both`,
  [sinMovimiento]: { animation: 'none' },
});
const claseCabo = css({
  animation: `${ola} 3.6s ease-in-out infinite`,
  [sinMovimiento]: { animation: 'none' },
});

const ANCHO = 320;
const ALTO = 64;

/**
 * El cabo con un nudo por recibo (los 12 últimos): los nudos se vuelven de
 * latón desde el cargo en que subió. Debajo, el primer y el último importe.
 */
export function CaboNudos({ serie }: { serie: SerieCobro }) {
  const { t } = useTranslation();
  const format = useFormat();
  const cargos = serie.cargos.slice(-12);
  if (cargos.length < 2) {
    return null;
  }
  const subida = subidaDe(serie);
  const min = Math.min(...cargos.map(c => c.importe));
  const max = Math.max(...cargos.map(c => c.importe));
  const paso = (ANCHO - 40) / (cargos.length - 1);
  const puntos = cargos.map((c, i) => {
    const x = 20 + i * paso;
    // El cabo cae un poco donde el recibo es mayor (más peso).
    const y = 26 + (max > min ? ((c.importe - min) / (max - min)) * 12 : 6);
    return { x, y, c };
  });
  const d = puntos
    .map((p, i) => {
      if (i === 0) {
        return `M${p.x} ${p.y}`;
      }
      const a = puntos[i - 1];
      const cx = (a.x + p.x) / 2;
      return `Q${cx} ${Math.max(a.y, p.y) + 6} ${p.x} ${p.y}`;
    })
    .join(' ');
  const primero = cargos[0];
  const ultimo = cargos[cargos.length - 1];

  return (
    <View
      role="img"
      aria-label={t('Last {{count}} charges, from {{first}} to {{last}}', {
        count: cargos.length,
        first: format(primero.importe, 'financial'),
        last: format(ultimo.importe, 'financial'),
      })}
      data-testid="gavi-cabo"
    >
      <svg
        viewBox={`0 0 ${ANCHO} ${ALTO}`}
        width="100%"
        height={ALTO}
        aria-hidden
        style={{ display: 'block', overflow: 'visible' }}
      >
        <g className={claseCabo}>
          <path
            d={d}
            fill="none"
            stroke={color.fg3}
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeDasharray="1 4.5"
          />
          <path
            d={d}
            fill="none"
            stroke={color.fg3}
            strokeOpacity=".45"
            strokeWidth="1.2"
          />
          {puntos.map(({ x, y, c }, i) => {
            const dorado = subida != null && c.fecha >= subida.fecha;
            return (
              <g
                key={c.id}
                className={claseNudo}
                style={{ animationDelay: `${i * 70}ms` }}
              >
                <circle
                  cx={x}
                  cy={y}
                  r={dorado ? 6.5 : 5.5}
                  fill={dorado ? color.warn : color.surface}
                  stroke={dorado ? color.warn : color.fg2}
                  strokeWidth="2"
                />
                <path
                  d={`M${x - 3} ${y - 2} Q${x} ${y + 3} ${x + 3} ${y - 2}`}
                  fill="none"
                  stroke={dorado ? color.surface : color.fg3}
                  strokeWidth="1.2"
                />
              </g>
            );
          })}
        </g>
        <text
          x={20}
          y={ALTO - 2}
          fontSize="11"
          fontWeight="700"
          fill={color.fg3}
        >
          {format(primero.importe, 'financial')}
        </text>
        <text
          x={ANCHO - 20}
          y={ALTO - 2}
          fontSize="11"
          fontWeight="800"
          textAnchor="end"
          fill={subida ? color.warn : color.fg2}
        >
          {format(ultimo.importe, 'financial')}
        </text>
      </svg>
    </View>
  );
}
